import styled from '@emotion/styled'

import { typography } from '@arcade/web'
import { Text } from '@cash-design-system/react'
import {
  createTokenizer,
  getTokenizerLinkProps,
  getUniqueKey,
  spacerTokenizer,
  Tokenizer,
  TokenizersRegex,
} from '@cash-web/text-tokenizer'

const LinkText = styled.a<{ typography: keyof typeof typography }>`
  ${props => props.theme.typography[props.typography]};
  color: ${props => props.theme.colors.textProminent};
  cursor: pointer;
  text-decoration: underline;
`

const disclaimerBoldTextTokenizer: Tokenizer = {
  regex: TokenizersRegex.bold,
  toToken([_, text]) {
    const uniqueKey = getUniqueKey()
    return (
      <Text color="textStandard" key={`bold-${uniqueKey}`} typography="labelMedium" as="strong">
        {text}
      </Text>
    )
  },
}

const createDisclaimerLinkTokenizer = (typographyToken: keyof typeof typography): Tokenizer => ({
  regex: TokenizersRegex.link,
  toToken: ([, text, href]: string[]) => {
    const uniqueKey = getUniqueKey()
    return (
      <LinkText typography={typographyToken} href={href} key={`link-${uniqueKey}`} {...getTokenizerLinkProps(href)}>
        {text}
      </LinkText>
    )
  },
})

const disclaimerBoldLinkTokenizer: Tokenizer = {
  regex: /\*\*(.*?)\[(.*?)\]\((.*?)\)(.*?)\*\*/,
  toToken: ([, beforeLink, linkText, href, afterLink]: string[]) => {
    const uniqueKey = getUniqueKey()
    return (
      <Text color="textStandard" key={`bold-link-${uniqueKey}`} typography="labelMedium" as="strong">
        {beforeLink}
        <LinkText typography="labelMedium" href={href} {...getTokenizerLinkProps(href)}>
          {linkText}
        </LinkText>
        {afterLink}
      </Text>
    )
  },
}

const createGeneralTextTokenizer = (typographyToken: keyof typeof typography): Tokenizer => ({
  regex: /.*/,
  toToken: ([text]: string[]) => {
    const uniqueKey = getUniqueKey()
    return (
      <Text key={`text-${uniqueKey}`} typography={typographyToken}>
        {text}
      </Text>
    )
  },
})

const createDisclaimerTextTokenizers = ({
  bodyTypography,
  linkTypography,
}: {
  bodyTypography: keyof typeof typography
  linkTypography: keyof typeof typography
}) => [
  disclaimerBoldLinkTokenizer,
  disclaimerBoldTextTokenizer,
  createDisclaimerLinkTokenizer(linkTypography),
  spacerTokenizer,
  createGeneralTextTokenizer(bodyTypography),
]

export const disclaimerTextTokenizer = createTokenizer(
  createDisclaimerTextTokenizers({ bodyTypography: 'bodySmall', linkTypography: 'linkSmall' })
)

export const structuredDisclosureTextTokenizer = createTokenizer(
  createDisclaimerTextTokenizers({ bodyTypography: 'bodyMedium', linkTypography: 'linkMedium' })
)

export const lineItemsTextTokenizer = createTokenizer([spacerTokenizer])
